import { api } from "@/lib/api";

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
const PARALLEL = 3;
const MAX_TRIES = 8;
const RETRYABLE = (s) => !s || s >= 500 || s === 408 || s === 429;

export class UploadBlocked extends Error {
  constructor(cause) {
    super(`blocked:${cause?.response?.status || cause?.code || "network"}`);
    this.code = cause?.response?.status || cause?.code || "network";
  }
}

export async function fingerprint(blob) {
  const MB = 1 << 20;
  try {
    const head = new Uint8Array(await blob.slice(0, MB).arrayBuffer());
    const tail = new Uint8Array(await blob.slice(Math.max(0, blob.size - MB)).arrayBuffer());
    const size = new TextEncoder().encode(String(blob.size));
    const buf = new Uint8Array(head.length + tail.length + size.length);
    buf.set(head); buf.set(tail, head.length); buf.set(size, head.length + tail.length);
    const hash = await crypto.subtle.digest("SHA-256", buf);
    return [...new Uint8Array(hash)].slice(0, 16).map((b) => b.toString(16).padStart(2, "0")).join("");
  } catch {
    return `${blob.size}-${blob.name || "rec"}-${blob.lastModified || 0}`.slice(0, 120);
  }
}

// Resumable chunked upload; ctrl = { paused, cancelled, abort } mutated by the caller.
export async function resumableUpload({ blob, filename, workspaceId, meta, ctrl, onState, fp: knownFp }) {
  const fp = knownFp || await fingerprint(blob);
  const { data: up } = await api.post("/uploads", { workspace_id: workspaceId, filename, size: blob.size, fingerprint: fp, meta });
  ctrl.uploadId = up.id;
  const got = new Set(up.received);
  const sizeOf = (i) => Math.min(up.chunk_size, blob.size - i * up.chunk_size);
  let done = [...got].reduce((a, i) => a + sizeOf(i), 0);
  onState({ status: done ? "resuming" : "uploading", loaded: done, total: blob.size, resumedFrom: done });
  const queue = [...Array(up.total_chunks).keys()].filter((i) => !got.has(i));
  const inflight = {};
  const report = (status) => onState({ status, loaded: done + Object.values(inflight).reduce((a, b) => a + b, 0) });
  ctrl.aborts = new Set();
  ctrl.abort = { abort: () => ctrl.aborts.forEach((c) => c.abort()) };
  const sendOne = async (i) => {
    let delay = 2000, tries = 0;
    for (;;) {
      if (ctrl.cancelled) throw new Error("cancelled");
      while (ctrl.paused && !ctrl.cancelled) { onState({ status: "paused", loaded: done }); await sleep(300); }
      if (ctrl.cancelled) throw new Error("cancelled");
      while (!navigator.onLine && !ctrl.cancelled) { onState({ status: "offline", loaded: done }); await sleep(1000); }
      const ac = new AbortController();
      ctrl.aborts.add(ac);
      try {
        const start = i * up.chunk_size;
        await api.put(`/uploads/${up.id}/chunks/${i}`, blob.slice(start, start + sizeOf(i)), {
          headers: { "Content-Type": "application/octet-stream" }, signal: ac.signal, timeout: 300000,
          onUploadProgress: (e) => { inflight[i] = Math.min(e.loaded, sizeOf(i)); report("uploading"); },
        });
        delete inflight[i];
        done += sizeOf(i);
        report("uploading");
        return;
      } catch (e) {
        delete inflight[i];
        if (ctrl.cancelled) throw new Error("cancelled");
        if (ctrl.paused) continue;
        if (!RETRYABLE(e.response?.status)) throw e;
        if (++tries >= MAX_TRIES) throw new UploadBlocked(e);
        onState({ status: "retrying", loaded: done });
        await sleep(delay);
        delay = Math.min(delay * 2, 30000);
      } finally { ctrl.aborts.delete(ac); }
    }
  };
  const worker = async () => { while (queue.length) await sendOne(queue.shift()); };
  await Promise.all(Array.from({ length: Math.min(PARALLEL, queue.length) }, worker));
  onState({ status: "finalizing", loaded: blob.size });
  return finalize(up.id, ctrl, onState);
}

const apiError = (status, detail) => Object.assign(new Error(detail || "Ralat"), { response: { status, data: { detail } } });

// Server assembles the file in the background; poll until done so long recordings never hit proxy timeouts.
async function finalize(uid, ctrl, onState) {
  for (let delay = 3000, tries = 0; ; delay = Math.min(delay * 2, 30000)) {
    let st;
    try { st = (await api.post(`/uploads/${uid}/complete`, null, { timeout: 120000 })).data; }
    catch (e) {
      if (!RETRYABLE(e.response?.status) || ctrl.cancelled) throw e;
      if (++tries >= MAX_TRIES) throw new UploadBlocked(e);
      onState({ status: "retrying" });
      await sleep(delay);
      continue;
    }
    while (st.status === "finalizing") {
      if (ctrl.cancelled) throw new Error("cancelled");
      await sleep(3000);
      try { st = (await api.get(`/uploads/${uid}/status`, { timeout: 30000 })).data; }
      catch (e) { if (!RETRYABLE(e.response?.status)) throw e; }
    }
    if (st.status === "done") return st.meeting;
    if (st.status === "failed") throw apiError(st.error_code || 400, st.error);
    if (++tries >= MAX_TRIES) throw apiError(st.error_code || 500, st.error);
    onState({ status: "retrying" });
    await sleep(delay);
  }
}
